import * as Sentry from '@sentry/browser';
import { AcceptCheck } from 'deckard-ui/dist/components/AcceptCheck/AcceptCheck';
import React, { Suspense, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'rsuite';
import { useTransientOccupancyTaxName } from '@/hooks/useTaxName';
import styled from 'styled-components';
import { AlertMsgTOTTabUnableToSubmitPayment } from '../../alert-string-constants';
import { BETA_FEATURES } from '../../constants';
import { isBetaFeatureEnabled } from '../../data/data-selectors';
import { strOracleApiSender } from '../../utils/stroracle-api-sender';
import { toaster } from '@/utils/toaster';
const RsModal = React.lazy(() => import('@/widgets/components/rs-modal'));
const RsModalHeader = React.lazy(
  () => import('@/widgets/components/rs-modal-header')
);
const RsModalBody = React.lazy(
  () => import('@/widgets/components/rs-modal-body')
);

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  color: #101938;
  margin-bottom: 0;
  padding: 0;
`;

export function AcceptCheckModalButton() {
  const [show, setShow] = useState(false);

  return (
    <>
      <Button onClick={() => setShow(true)}>Accept Check</Button>
      <AcceptCheckModal show={show} setShow={setShow} />
    </>
  );
}

export function AcceptCheckModal({
  show,
  setShow,
  uniqueCode,
  onHideModal,
  allowOtherPaymentTypes,
}) {
  const isFeatureFlagEnabled = useSelector(
    isBetaFeatureEnabled(BETA_FEATURES.acceptChecks)
  );

  const onHide = useCallback(() => {
    setShow(false);
    if (onHideModal) {
      onHideModal();
    }
  }, [setShow, onHideModal]);

  const fetchCheckPaymentByUniqueCode = useCallback(async (uniqueCode) => {
    try {
      const { data } = await strOracleApiSender.get(
        `/checkPayments/${uniqueCode}`
      );

      return data;
    } catch (e) {
      if (e.response.status === 404) {
        return null;
      }

      throw e;
    }
  }, []);

  const acknowledgePayment = useCallback(
    async ({ paymentId, paymentMethod, referenceNumber, signatory }) => {
      try {
        const { data } = await strOracleApiSender.post(
          `/acknowledgePayment/${paymentId}`,
          {
            signatory,
            payment_method: paymentMethod,
            reference_number: referenceNumber,
          }
        );

        toaster.success('Check accepted successfully');

        return data;
      } catch (e) {
        Sentry.captureException(e);
        toaster.error(AlertMsgTOTTabUnableToSubmitPayment);
        throw e;
      }
    },
    []
  );

  const totTaxName = useTransientOccupancyTaxName();

  if (!isFeatureFlagEnabled) {
    return null;
  }

  return (
    <Suspense fallback={<div />}>
      <RsModal open={show} onClose={onHide} overflow={false}>
        <RsModalHeader>
          <Title>Accept Check</Title>
        </RsModalHeader>
        <RsModalBody>
          <AcceptCheck
            fetchCheckPaymentCallback={fetchCheckPaymentByUniqueCode}
            acknowledgePaymentCallback={acknowledgePayment}
            acknowledgePaymentConfig={{
              personsDropdown: [{ id: 'registrant', label: 'Registrant' }],
            }}
            handleClose={onHide}
            totTaxName={totTaxName}
            uniqueCode={uniqueCode}
            allowOtherPaymentTypes={allowOtherPaymentTypes}
          />
        </RsModalBody>
      </RsModal>
    </Suspense>
  );
}
